import { useState } from 'react';
import useCourseTheme from '../../core/hooks/useCourseTheme';
import { useLocation } from 'react-router-dom';
import { Box, ButtonBase, SwipeableDrawer, Typography } from '@mui/material';
import HomeRoundedIcon from '@mui/icons-material/HomeRounded';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import ScienceIcon from '@mui/icons-material/Science';
import { useNavigationCore } from '../../core/router/navigation';
import { INICIO } from '../../core/router/routes';
import useCourseSections from './useCourseSections';

const ACCENT = '#4F46E5';
export const MOBILE_TAB_BAR_HEIGHT = 64;

/** Pestañas fijas abajo en el celular (reemplazan la barra negra para los alumnos). */
const MobileTabBar = () => {
  const { pathname } = useLocation();
  const { goToInicio } = useNavigationCore();
  const { sections, tools } = useCourseSections();
  const [moreOpen, setMoreOpen] = useState(false);

  // Abajo caben Inicio + 3 secciones + "Más"; el resto (foro, alianzas, herramientas) va en "Más"
  const main = sections.filter((section) => ['classes', 'calendar', 'reports'].includes(section.key));
  const rest = sections.filter((section) => !main.includes(section));
  const hasMore = rest.length > 0 || tools.length > 0;

  const isActive = (path: RegExp) => path.test(pathname);
  const courseTheme = useCourseTheme();
  const accent = courseTheme?.main ?? ACCENT;
  const accentSoft = courseTheme?.tint ?? '#EEF2FF';
  const tab = (key: string, label: string, icon: React.ReactNode, active: boolean, onClick: () => void, tour?: string) => (
    <ButtonBase
      key={key}
      onClick={onClick}
      data-tour={tour}
      sx={{ flex: 1, flexDirection: 'column', gap: 0.25, color: active ? accent : '#64748B', '& svg': { fontSize: 24 } }}
    >
      {icon}
      <Typography sx={{ fontSize: 11, fontWeight: 600 }}>{label}</Typography>
    </ButtonBase>
  );

  return (
    <>
      <Box
        component="nav"
        sx={{
          position: 'fixed',
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: (theme) => theme.zIndex.appBar,
          display: 'flex',
          height: `calc(${MOBILE_TAB_BAR_HEIGHT}px + env(safe-area-inset-bottom, 0px))`,
          pb: 'env(safe-area-inset-bottom, 0px)',
          backgroundColor: '#fff',
          borderTop: '1px solid #E2E8F0',
        }}
      >
        {tab('inicio', 'Inicio', <HomeRoundedIcon />, pathname === INICIO, goToInicio)}
        {main.map((section) => tab(section.key, section.label, section.icon, isActive(section.path), section.onClick, section.tour))}
        {hasMore ? tab('more', 'Más', <MoreHorizIcon />, moreOpen, () => setMoreOpen(true), 'tools') : null}
      </Box>

      <SwipeableDrawer
        anchor="bottom"
        // Sin esto la zona para deslizar queda encima de las pestañas y no se pueden tocar
        disableSwipeToOpen
        open={moreOpen}
        onOpen={() => setMoreOpen(true)}
        onClose={() => setMoreOpen(false)}
        PaperProps={{ sx: { borderRadius: '20px 20px 0 0', px: 2.5, pt: 1.25, pb: `calc(24px + env(safe-area-inset-bottom, 0px))` } }}
      >
        <Box sx={{ width: 40, height: 4, borderRadius: 2, backgroundColor: '#CBD5E1', mx: 'auto', mb: 2 }} />
        <Typography sx={{ fontWeight: 800, fontSize: 17, mb: 1 }}>Más del curso</Typography>
        {[
          ...rest.map((section) => ({ key: section.key, label: section.label, icon: section.icon, onClick: section.onClick })),
          ...tools.map((t) => ({ key: t.label, label: t.label, icon: <ScienceIcon />, onClick: t.onClick })),
        ].map((item) => (
          <ButtonBase
            key={item.key}
            onClick={() => { setMoreOpen(false); item.onClick(); }}
            sx={{ width: '100%', justifyContent: 'flex-start', gap: 2, py: 1.5, borderTop: '1px solid #F1F5F9', textAlign: 'left' }}
          >
            <Box sx={{ width: 38, height: 38, borderRadius: 2.5, backgroundColor: accentSoft, color: accent, display: 'grid', placeItems: 'center', '& svg': { fontSize: 20 } }}>
              {item.icon}
            </Box>
            <Typography sx={{ fontWeight: 600, fontSize: 15 }}>{item.label}</Typography>
          </ButtonBase>
        ))}
      </SwipeableDrawer>
    </>
  );
};

export default MobileTabBar;
